/**
 * Intelligence Evolution Timeline
 * ---------------------------------------------------------------
 * Cycle-by-cycle history of the Adaptive Learning Engine. Shows
 * what the engine learned, what changed afterward, and the
 * resulting behavioral performance delta.
 *
 * Forensic evolution log — not analytics charts.
 */

import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { GitCommit, Loader2, TrendingUp, TrendingDown, Activity } from "lucide-react";

interface CycleRow {
  id: string;
  cycle_number: number;
  committed_at: string;
  asset_count: number;
  bias_count: number;
  ranking_count: number;
  hook_count: number;
  summary: any;
  deltas: { label: string; delta: number }[] | any;
}

const titleCase = (s: string) =>
  (s ?? "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export default function IntelligenceEvolutionTimeline() {
  const [cycles, setCycles] = useState<CycleRow[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await (supabase as any)
      .from("learning_cycles")
      .select("id, cycle_number, committed_at, asset_count, bias_count, ranking_count, hook_count, summary, deltas")
      .order("cycle_number", { ascending: false })
      .limit(40);
    setCycles((data ?? []) as CycleRow[]);
    setLoading(false);
  }, []);

  useEffect(() => { void load(); }, [load]);

  // Live update on commit
  useEffect(() => {
    const onCommit = () => { void load(); };
    window.addEventListener("intelligence-committed", onCommit);
    const id = window.setInterval(() => { void load(); }, 60_000);
    return () => {
      window.removeEventListener("intelligence-committed", onCommit);
      window.clearInterval(id);
    };
  }, [load]);

  return (
    <Card className="bg-charcoal-medium/60 border-glass-border backdrop-blur-sm relative overflow-hidden">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-cyan/60 to-transparent animate-pulse" />
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-base flex items-center gap-2">
            <GitCommit className="w-4 h-4 text-cyan animate-pulse" />
            Intelligence Evolution Timeline
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono ml-1">
              Adaptive learning cycles
            </span>
          </CardTitle>
          <Badge variant="outline" className="border-cyan/40 text-cyan font-mono text-[10px]">
            <Activity className="w-3 h-3 mr-1" />
            {cycles.length} CYCLES
          </Badge>
        </div>
      </CardHeader>

      <CardContent className="p-0">
        {loading ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="w-5 h-5 animate-spin text-cyan" />
          </div>
        ) : cycles.length === 0 ? (
          <div className="px-6 py-10 text-center text-[11px] uppercase tracking-wider font-mono text-muted-foreground">
            No learning cycles committed · run Commit Intelligence to begin evolution
          </div>
        ) : (
          <ScrollArea className="max-h-[500px]">
            <div className="relative pl-10 pr-4 py-3">
              {/* vertical timeline rail */}
              <div className="absolute left-5 top-0 bottom-0 w-px bg-gradient-to-b from-cyan/40 via-cyan/15 to-transparent" />

              {cycles.map((c) => {
                const deltas: { label: string; delta: number }[] = Array.isArray(c.deltas) ? c.deltas : [];
                const summary = (c.summary ?? {}) as any;
                return (
                  <div key={c.id} className="relative mb-5 last:mb-0">
                    {/* node */}
                    <div className="absolute -left-[22px] top-1.5 flex items-center justify-center">
                      <span className="w-2.5 h-2.5 rounded-full bg-cyan ring-4 ring-cyan/20 animate-pulse" />
                    </div>

                    <div className="rounded-md border border-glass-border bg-charcoal-deep/60 p-3 space-y-2">
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-bold uppercase tracking-wider text-cyan font-mono">
                            Cycle {c.cycle_number}
                          </span>
                          <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono">
                            {new Date(c.committed_at).toLocaleString()}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-[9px] font-mono uppercase tracking-wider text-muted-foreground">
                          <span>{c.asset_count} signals</span>
                          <span>·</span>
                          <span>{c.bias_count} biases</span>
                          <span>·</span>
                          <span>{c.hook_count} hooks</span>
                        </div>
                      </div>

                      {/* what was learned */}
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 pt-1">
                        {[
                          { label: "Top archetype",   v: summary.top_archetype },
                          { label: "Top format",      v: summary.top_format },
                          { label: "Top escalation",  v: summary.top_escalation },
                        ].map((row) => (
                          <div key={row.label} className="rounded border border-glass-border/60 bg-black/30 px-2 py-1.5">
                            <div className="text-[9px] uppercase tracking-wider text-muted-foreground font-mono">{row.label}</div>
                            <div className="text-xs font-mono text-foreground uppercase tracking-wider truncate">
                              {row.v ? titleCase(row.v) : "—"}
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* performance delta */}
                      {deltas.length > 0 && (
                        <div className="pt-2 border-t border-glass-border">
                          <div className="text-[9px] uppercase tracking-[0.2em] text-muted-foreground font-mono mb-1.5">
                            Performance delta vs prior cycle
                          </div>
                          <div className="flex flex-wrap gap-x-4 gap-y-1">
                            {deltas.map((d) => {
                              const up = d.delta > 0;
                              const flat = d.delta === 0;
                              const Icon = up ? TrendingUp : TrendingDown;
                              return (
                                <div key={d.label} className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider">
                                  <span className="text-muted-foreground">{d.label}</span>
                                  {flat ? (
                                    <span className="text-muted-foreground">—</span>
                                  ) : (
                                    <span className={`inline-flex items-center gap-0.5 ${up ? "text-emerald-300" : "text-red-300"}`}>
                                      <Icon className="w-3 h-3" />
                                      {up ? "+" : ""}{d.delta}%
                                    </span>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </ScrollArea>
        )}
      </CardContent>
    </Card>
  );
}
